import { useEffect, useState } from 'react';

import ClickablePDP from '@v2/components/common/presentational/clickables/pdp';
import { useAddCartModal, useOutOfStockModal } from '@components/common/presentational/modal/common';
import { PackQuantityInput } from '@components/common/products/quantity';

import { IProduct, ICartBox } from '@interfaces/_cart';
import Star from '@v2/components/common/recommendations/star';
import { useIsSwapping, useAddPackAction, useBuyingForBusiness } from '@v2/components/common/products/utils';
import { WPImage } from '@v2/utils/image';
import { useGTMItemDispatch } from '@v2/utils/gtm';
import { useEntity } from '@app/store/rtk.entities.selectors';
import { useCartAllBoxes, usePackBoxIndex } from '@app/store/cart.selectors';
import Tooltip from '@v2/components/common/presentational/tooltip/tooltip';
import Discontinuation from '@v2/components/common/products/discontinuation';

export default function Pack({ product, disabled, subscribed, filterAge, staticPDP }:
    { product: IProduct, disabled: boolean, subscribed?: boolean, filterAge?: any, staticPDP?: boolean }) {
    const gtmDispatch = useGTMItemDispatch();
    const addPackAction = useAddPackAction();
    const blueprints = useEntity('blueprints');
    const ageBundles = useEntity('ageBundles');
    const packBoxIndex = usePackBoxIndex(product.id);
    const isSwapping = useIsSwapping();

    const boxes = useCartAllBoxes();
    const [ageLabel, setAgeLabel] = useState(null);

    const [showCartModal] = useAddCartModal(packBoxIndex);
    const [showOutOfStockModal] = useOutOfStockModal(product);
    const {showBuyingForBusinessModal, buyingMoreThanTen} = useBuyingForBusiness();

    const onPackAdd = () => {
        if (buyingMoreThanTen) {
            showBuyingForBusinessModal();
        } else {
            addPackAction(packBoxIndex,
                product,
                blueprints,
                boxes,
                subscribed);

            if (!isSwapping) {
                showCartModal();
            }
        }
    }

    useEffect(() => {
        setAgeLabel(null);

        if (filterAge === null || !ageBundles) {
            return;
        }

        const bundles: IProduct[] = Object.values(ageBundles);

        if (!bundles[filterAge]) {
            return;
        }

        bundles[filterAge].products.forEach((p1: IProduct) => {
            if (product.title === p1.title) {
                setAgeLabel(bundles[filterAge].title.replace(' Bundle', ''));
                return;
            }
        });
    }, [filterAge]);

    const select = () => {
        gtmDispatch('select_item', product);
    }

    return (
        <div className={`product ${product.availability === 'outofstock' ? 'out-of-stock' : ''}`}>
            <ClickablePDP
                product={product}
                useHistoryNavigation={(staticPDP || isSwapping) ? false : true}
                onBeforeClick={select}>
                <div className="product-link vertical">
                    <div className="image">
                        <WPImage src={product.mainImage}
                        alt={product.mainImageAlt || product.title} />
                    </div>
                    <div className="title">
                        {product.title}
                    </div>
                    <div className="quantity">
                        {product.size}
                        {['pouchpacks', 'smoothiepacks', 'bonebrothpouchpacks'].includes(product.category)
                            ? `6-pack`
                            : (product.category === 'barpacks'
                                ? '5-pack'
                                : `items`)}
                    </div>
                </div>
            </ClickablePDP>

            <div className="info">
                {subscribed
                    ?
                    <>
                        <span className="regular">${product.price.toFixed(2)}</span>
                        <Tooltip
                            showOnClick={true}
                            className="tooltip"
                            width={250}
                            description={`Optional Subscribe & Save for an additional 15% off`}>
                            <span className="discount">${product.priceSubscribed.toFixed(2)}</span>
                        </Tooltip>
                    </>
                    :
                    <span className="default">${product.price.toFixed(2)}</span>
                }
            </div>

            {product.availability === 'outofstock' ?
                <div className="out-of-stock-button">
                    <button
                        tabIndex={0}
                        onClick={showOutOfStockModal}
                        className="button secondary blue">NOTIFY ME</button>
                </div>
                :
                <>
                    {boxes[packBoxIndex]
                        ?
                        <PackQuantityInput box={boxes[packBoxIndex]} />
                        :
                        <div className={`action add ${disabled ? 'd-none' : ''}`}>
                            <button
                                className="button primary blue"
                                onClick={onPackAdd}
                                disabled={disabled}
                                aria-label={'Add to bag'}>
                                {isSwapping ? 'Select' : 'Add to bag'}
                            </button>
                        </div>
                    }
                </>
            }
            <div className="out-of-stock-info">out of stock!</div>
            <div className="recommended-info">Recommended</div>
            {ageLabel && product.availability !== 'outofstock' &&
                <div className="age-bundle-nfo">{ageLabel}</div>
            }
            {!ageLabel && product.availability !== 'outofstock' &&
                <Discontinuation productId={product.id}/>
            }
            <span className="stars mobile">
                {Object.values(boxes).map((box: ICartBox, index: number) =>
                    box.child && box.stage && box.stage.lines && box.child.recommendedPacksEnabled &&
                    Object.values(!box.child.isVegan ? box.stage.lines : box.stage.veggieLines).map((line: any, key: any) =>
                        line.product.title.toLowerCase() === product.title.toLowerCase().replace(' pack', '') &&
                        <Star key={key} name={box.child.name} index={index} />
                    )
                )}
            </span>
        </div>
    );
}